import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { compose } from 'redux';

import { reloadCompany } from 'common/actions/company';
import AJAX from 'common/AJAX';
import { ActiveIntegrationContext } from 'common/containers/ActiveIntegrationsContainer';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { OpenModalContext } from 'common/containers/ModalContainer';
import { LocationContext, RouterContext } from 'common/containers/RouterContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import connect from 'common/core/connect';
import Helmet from 'common/helmets/Helmet';
import Button from 'common/inputs/Button';
import AccessModal from 'common/modals/AccessModal';
import ConfirmModal from 'common/modals/ConfirmModal';
import { RequiredNewChannelParams } from 'common/routeComponents/landing/redirect/MicrosoftTeamsInstall';
import withAccessControl from 'common/routing/withAccessControl';
import AdminFeatureUpsell from 'common/subdomain/admin/AdminFeatureUpsell';
import AdminIntegrationChannelSettings from 'common/subdomain/admin/AdminIntegrationChannelSettings';
import AdminIntegrationLimitUpsell from 'common/subdomain/admin/AdminIntegrationLimitUpsell';
import hasPermission from 'common/util/hasPermission';
import { RoutePermissions, testEveryPermission } from 'common/util/permissions';
import withContexts from 'common/util/withContexts';

import 'css/components/subdomain/admin/_AdminMicrosoftTeamsSettings.scss';

const HelpGuideLink = 'https://help.canny.io/en/articles/4343053-microsoft-teams-integration';
const IntegrationDescription =
  'Get instant notifications on select events in Canny such as new posts, comments, or votes.';
const TeamsAppLink = 'https://teams.microsoft.com/l/app/0d13dcaa-f470-4cac-b9ce-d80ab602feba';

class AdminMicrosoftTeamsSettings extends Component {
  static propTypes = {
    company: PropTypes.shape({
      _id: PropTypes.string,
      subdomain: PropTypes.string,
      microsoftTeamsChannels: PropTypes.array,
    }),
    location: PropTypes.shape({
      query: PropTypes.object,
    }),
    openModal: PropTypes.func,
    reloadCompany: PropTypes.func,
    router: PropTypes.object,
    viewer: PropTypes.object,
  };

  state = {
    error: null,
    isInstalled: this.props.company.microsoftTeamsChannels.length > 0,
  };

  componentDidMount() {
    this.checkPermission();
    this.checkAddNewChannel();
  }

  checkAddNewChannel() {
    const { location, openModal, router } = this.props;
    const query = { ...location.query };

    // Remove query from the url
    router.replace({ pathname: location.pathname });

    // If plan doesn't support MS Teams, don't show the confirmation modal.
    // They'll see the upsell info instead.
    if (!this.doesPlanSupport()) {
      return;
    }

    const missingParams = RequiredNewChannelParams.filter((param) => !query[param]);
    if (missingParams.length === RequiredNewChannelParams.length) {
      // Missing all params. Likely they did not come from the Microsoft Teams iframe.
      return;
    }

    if (missingParams.length > 0) {
      this.setState({
        error: `Missing required channel params: ${missingParams.join(', ')}`,
      });
      return;
    }

    const config = RequiredNewChannelParams.reduce((map, param) => {
      map[param] = query[param];
      return map;
    }, {});

    const channelNameString = config.channelName ? `"${config.channelName}"` : 'selected';
    openModal(ConfirmModal, {
      message: `Do you want to send notifications to the ${channelNameString} Microsoft Teams channel?`,
      onConfirm: () => this.onConnect(config),
    });
  }

  checkPermission() {
    const { company, openModal, router, viewer } = this.props;
    if (!hasPermission('manageIntegrations', company, viewer)) {
      router.replace('/admin');
      openModal(
        AccessModal,
        {
          requiredPermissions: ['manageIntegrations'],
        },
        {
          allowRouteChange: true,
        }
      );
    }
  }

  doesPlanSupport() {
    const { company } = this.props;
    return company?.integrations?.microsoftTeams;
  }

  onConnect = async (config) => {
    const { reloadCompany } = this.props;
    const response = await AJAX.post('/api/microsoftTeams/registerChannel', {
      ...config,
    });

    if (response.includes('invalid webhook URL')) {
      this.setState({
        error: 'Invalid channel webhook. Please contact our team for support.',
      });
      return;
    }

    if (response !== 'success') {
      this.setState({
        error: 'Something went wrong, please contact our team for support.',
      });
      return;
    }

    await reloadCompany();

    this.setState({
      error: null,
      isInstalled: true,
    });
  };

  renderChannels() {
    const {
      boards,
      company: { microsoftTeamsChannels },
      reloadCompany,
    } = this.props;
    if (microsoftTeamsChannels.length < 1) {
      return null;
    }

    const integration = {
      deleteURL: '/api/microsoftTeams/deleteChannel',
      editURL: '/api/microsoftTeams/updateChannel',
      getChannelHeading: (channel) =>
        channel.teamName && channel.channelName
          ? `${channel.teamName} : ${channel.channelName}`
          : `Microsoft Teams connected channel`,
      reload: reloadCompany,
    };

    return (
      <div>
        {microsoftTeamsChannels.map((channel) => (
          <AdminIntegrationChannelSettings
            boards={boards}
            channel={channel}
            integration={integration}
            key={channel._id}
          />
        ))}
      </div>
    );
  }

  renderInfo() {
    return (
      <div>
        <div className="subheading">Send notifications to channels in Microsoft Teams</div>
        <p className="text">{IntegrationDescription}</p>
        <p className="text">
          Install the Microsoft Teams Connector to get started. Check out our{' '}
          <a
            className="link"
            href={HelpGuideLink}
            target="_blank"
            rel="nofollow noreferrer noopener">
            setup guide
          </a>{' '}
          for more info.
        </p>
      </div>
    );
  }

  renderDisconnected() {
    const {
      activeIntegrations: { integrationCount, integrationLimit },
    } = this.props;
    if (!this.state.isInstalled && integrationLimit && integrationCount >= integrationLimit) {
      return <AdminIntegrationLimitUpsell />;
    }

    if (!this.doesPlanSupport()) {
      return <AdminFeatureUpsell feature="microsoftTeams" />;
    }

    return (
      <a
        className="buttonLink"
        href={TeamsAppLink}
        target="_blank"
        rel="nofollow noreferrer noopener">
        <Button value={this.state.isInstalled ? 'Add More Channels' : 'Add to Microsoft Teams'} />
      </a>
    );
  }

  renderError() {
    const { error } = this.state;
    if (!error) {
      return null;
    }

    return <div className="error text">{this.state.error}</div>;
  }

  render() {
    return (
      <div className="adminMicrosoftTeamsSettings">
        <Helmet title="Microsoft Teams Integration | Canny" />
        <div className="settingsHeading">Microsoft Teams Integration</div>
        <div className="content">
          {this.renderInfo()}
          {this.renderDisconnected()}
          {this.renderError()}
          {this.renderChannels()}
        </div>
      </div>
    );
  }
}

export default compose(
  connect(null, (dispatch) => ({
    reloadCompany: () => dispatch(reloadCompany()),
  })),
  withAccessControl(
    testEveryPermission(RoutePermissions.integrations.microsoftTeams),
    '/admin/settings',
    { forwardRef: true }
  ),
  withContexts(
    {
      activeIntegrations: ActiveIntegrationContext,
      boards: PropTypes.array,
      company: CompanyContext,
      location: LocationContext,
      openModal: OpenModalContext,
      router: RouterContext,
      viewer: ViewerContext,
    },
    {
      forwardRef: true,
    }
  )
)(AdminMicrosoftTeamsSettings);
